import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { Button } from "./ui/button";

interface NewsItem {
  title: string;
  content: string;
  creator: string;
}

const fetchNews = async () => {
  // Use a CORS proxy to fetch the RSS feed
  const corsProxy = "https://api.allorigins.win/raw?url=";
  const response = await fetch(corsProxy + encodeURIComponent('https://cfrradio.com/blog/feed/'));
  const text = await response.text();
  const parser = new DOMParser();
  const xml = parser.parseFromString(text, 'text/xml');
  const items = xml.querySelectorAll('item');
  
  return Array.from(items).map(item => {
    const titleElement = item.querySelector('title');
    // Use correct namespace handling for content:encoded
    const contentElement = item.getElementsByTagNameNS('*', 'encoded')[0] || item.querySelector('content\\:encoded');
    // Use correct namespace handling for dc:creator
    const creatorElement = item.getElementsByTagNameNS('*', 'creator')[0] || item.querySelector('dc\\:creator');
    
    return {
      title: titleElement?.textContent || 'Untitled',
      content: contentElement?.textContent || 'No content available',
      creator: creatorElement?.textContent || 'Unknown Author'
    };
  });
};

interface NewsOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewsOverlay = ({ open, onOpenChange }: NewsOverlayProps) => {
  const [selectedArticle, setSelectedArticle] = useState<NewsItem | null>(null);
  
  const { data: newsItems = [], isLoading, isError } = useQuery({
    queryKey: ['news'],
    queryFn: fetchNews,
    retry: 2
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
        <SheetHeader className="relative">
          {selectedArticle ? (
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute left-2 top-2"
              onClick={() => setSelectedArticle(null)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          ) : (
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute right-2 top-2"
              onClick={() => onOpenChange(false)}
            >
              <ArrowLeft className="h-4 w-4" rotate={180} />
            </Button>
          )}
        </SheetHeader>

        {selectedArticle ? (
          <div className="space-y-6 pt-6">
            <h2 className="text-2xl font-bold mt-[15px]">{selectedArticle.title}</h2>
            <div dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
            <div className="mt-8 p-4 bg-secondary rounded-lg">
              <p className="text-sm font-medium">Reporter: {selectedArticle.creator}</p>
            </div>
          </div>
        ) : (
          <Table>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell className="text-center">Loading news...</TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell className="text-center text-red-500">Error loading news. Please try again later.</TableCell>
                </TableRow>
              ) : (
                newsItems.map((item, index) => (
                  <TableRow 
                    key={index}
                    className="cursor-pointer hover:bg-secondary"
                    onClick={() => setSelectedArticle(item)}
                  >
                    <TableCell className="text-lg">{item.title}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </SheetContent>
    </Sheet>
  );
};